import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { getErrorDetail } from "components/utility/utils";
import { fetchOrchidAPI } from "utils/api";
import buildUrlWithParams from "./buildUrlWithParams";

export type CLIENT_STATUS =
  | "patient"
  | "inactive"
  | "discharged"
  | "waitlist"
  | "non-patient";

export type PATIENT_TYPE = "all" | "individual" | "couple";

export type SORT_BY = "first_name" | "last_name";

const PAGE_SIZE = 100;

const patientChartQueryOptions = (params: {
  skip?: number;
  limit?: number;
  patientType?: PATIENT_TYPE;
  clientStatus?: CLIENT_STATUS[];
  search?: string;
  sortBy?: SORT_BY;
}) => {
  return queryOptions({
    queryKey: ["patientChart", params],
    queryFn: async ({ signal }) => {
      const url = buildUrlWithParams("/api/users/v1/patient_chart/", params);

      const res = await fetchOrchidAPI(url, { signal });
      if (!res.ok) {
        throw new Error(
          `failed to list patient chart: ${getErrorDetail(await res.text())}`,
        );
      }
      const json = await res.json();
      return json as {
        limit: number;
        entries: PatientChartResponseEntry[];
        skip: number;
        total_count: number;
        stats: PatientChartResponseStats;
      };
    },
  });
};

export const patientChartInfiniteQueryOptions = (params: {
  patientType?: PATIENT_TYPE;
  clientStatus?: CLIENT_STATUS[];
  search?: string;
  sortBy?: SORT_BY;
  onBehalfOf?: string;
}) => {
  return infiniteQueryOptions({
    queryKey: ["patientChart", params],
    queryFn: async ({ pageParam, signal }) => {
      const url = buildUrlWithParams("/api/users/v1/patient_chart/", {
        ...params,
        limit: PAGE_SIZE,
        skip: pageParam * PAGE_SIZE,
      });

      const res = await fetchOrchidAPI(url, { signal });
      if (!res.ok) {
        throw new Error(
          `failed to list patient chart: ${getErrorDetail(await res.text())}`,
        );
      }
      const json = await res.json();
      return json as {
        limit: number;
        entries: PatientChartResponseEntry[];
        skip: number;
        total_count: number;
        stats: PatientChartResponseStats;
      };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const hasMore = pages.length * PAGE_SIZE < lastPage.total_count;
      if (!hasMore) {
        return undefined;
      }
      return pages.length;
    },
  });
};

export default patientChartQueryOptions;
